<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="primary" color="textSecondary">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <div v-if="isAuthenticated" class="flex gap-2">
              <VaAvatar size="small" color="danger" class="mr-0">
                {{ getUserInitials }}
              </VaAvatar>
            </div>
            <div v-else class="flex gap-2">
              <button class="btn btn-primary btn-sm" @click="loginPopup">Login</button>
            </div>
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <VaList v-for="group in options" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(`user.${group.name}`) }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="handleItemClick(item)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`user.${item.name}`) }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useIsAuthenticated, useMsal } from 'vue3-msal-plugin'
import { useUserStore } from '../../../../stores/user' // Adjust path as needed
import { useAuthStore } from '../../../../stores/auth-store'
import { useRouter } from 'vue-router'
import { useToast } from 'vuestic-ui' // For notifications

const { instance, accounts, inProgress, loginRequest } = useMsal()
const userStore = useUserStore()
const authStore = useAuthStore()
const router = useRouter()
const { init: initToast } = useToast()

console.log('instance', instance)
console.log('accounts', accounts.value)
console.log('inProgress', inProgress.value)

const isLoading = ref(false)
const isAuthenticated = useIsAuthenticated()

// Initialize user from storage on component mount
onMounted(async () => {
  if (isAuthenticated.value) {
    // Try to restore session from storage
    const restored = await userStore.initializeFromStorage()
    if (restored) {
      console.log('User session restored from storage')
    }
  }
})

// Watch for authentication changes and update user store
watch(
  [accounts, isAuthenticated],
  ([newAccounts, authenticated]) => {
    if (authenticated && newAccounts && newAccounts.length > 0 && !userStore.isValidated) {
      const account = newAccounts[0]
      userStore.updateUser({
        oid: account.localAccountId || account.homeAccountId,
        name: account.name || account.username || 'User',
        email: account.username || '',
        username: account.username || '',
      })
      localStorage.setItem('token', account.idToken ?? '')
      authStore.setToken(account.idToken ?? '')
      authStore.setUserDataToSession(account.idToken ?? '', account.username ?? '')
    } else if (!authenticated && userStore.user) {
      userStore.clearUser()
    }
  },
  { immediate: true },
)

// Computed property to get user initials
const getUserInitials = computed(() => {
  if (userStore.user?.name) {
    return userStore.user.name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('')
  }

  // Fallback to account info if user store is empty
  if (accounts.value && accounts.value.length > 0) {
    const account = accounts.value[0]
    const name = account.name || account.username || 'User'
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('')
  }

  return 'U'
})

const loginPopup = async () => {
  try {
    console.log('Starting the login process')

    //step 1: MSAL Login
    const response = await instance.loginPopup(loginRequest)
    console.log('MSAL Login Successful:', response)

    if (response && response.accessToken && response.account) {
      //STEP 2: Store the Initial User data with token
      userStore.updateUser({
        oid: response.account.localAccountId || response.account.homeAccountId,
        name: response.account.name || response.account.username,
        email: response.account.username || '',
        username: response.account.username || '',
        access_token: response.accessToken,
      })
      //STEP 3: Validate the user with the backend and get other details
      try {
        await userStore.validateUserWithBackend(
          response.accessToken,
          response.account.localAccountId || response.account.homeAccountId,
          response.account.username,
        )

        initToast({
          message: 'Login successful!',
          color: 'success',
        })

        // Optional: Redirect to dashboard or home
        router.push({ name: 'dashboard' })
      } catch (backendError) {
        console.error('Backend validation failed:', backendError)

        initToast({
          message: 'Login successful but failed to load user permissions. Please try refreshing.',
          color: 'warning',
        })
      }
    }
  } catch (error) {
    console.error('Login failed:', error)

    initToast({
      message: 'Login failed. Please try again.',
      color: 'danger',
    })

    userStore.clearUser()
  } finally {
    isLoading.value = false
    isShown.value = false
  }
}

const logoutPopup = () => {
  instance.logoutPopup({
    mainWindowRedirectUri: '/',
  })
}

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

withDefaults(
  defineProps<{
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'account',
        separator: true,
        list: [
          {
            name: 'profile',
            to: 'preferences',
            icon: 'mso-account_circle',
          },
        ],
      },
      {
        name: '',
        separator: false,
        list: [
          {
            name: 'logout',
            icon: 'mso-logout',
          },
        ],
      },
    ],
  },
)

const isShown = ref(false)

const handleItemClick = (item: ProfileListItem) => {
  if (item.name === 'logout') {
    logoutPopup()
    isShown.value = false // Close dropdown after logout
  }
}

const resolveLinkAttribute = (item: ProfileListItem) => {
  if (item.name === 'logout') {
    return {}
  }
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}
</style>
